// Busca tolerante dos menus da barra lateral. Sem React: recebe as rotas que o
// usuário já pode ver e devolve as que batem com o que foi digitado, na ordem do
// menu.
//
// Um termo bate num campo (nome, palavras-chave `busca` ou grupo) por, do mais
// forte ao mais fraco: começo de palavra, trecho, erro de digitação (1 a partir
// de 4 letras, 2 a partir de 7) e letras na ordem (a partir de 3 letras). Todos os
// termos digitados precisam bater em algum campo. Se algum menu bateu por começo
// ou trecho ("forte"), os que só bateram por erro de digitação ou letras na ordem
// ("fraco") ficam de fora, para consulta curta não poluir a lista.

const PESOS = { nome: 3, busca: 2, group: 1 };
const PONTOS = { prefixo: 100, trecho: 60, digitacao: 40, ordem: 20 };
const MIN_DIGITACAO = 4;
const MIN_ORDEM = 3;
const MAX_BURACOS = 5;

// Minúsculas, sem acento e com tudo que não é letra ou número virando espaço. Um
// caractere vira exatamente um caractere, para os índices dos trechos destacados
// valerem no texto original.
export function normalizar(texto) {
    const original = String(texto ?? '');
    let saida = '';

    for (let i = 0; i < original.length; i++) {
        const ch = original[i].toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
        saida += /^[a-z0-9]$/.test(ch) ? ch : ' ';
    }

    return saida;
}

// Distância de edição (Damerau-Levenshtein restrita: troca, inserção, remoção e
// transposição de vizinhos). Devolve `limite + 1` sem calcular quando só a
// diferença de tamanho já passa do limite.
export function distancia(a, b, limite = Infinity) {
    if (Math.abs(a.length - b.length) > limite) return limite + 1;

    const d = [];
    for (let i = 0; i <= a.length; i++) d[i] = [i];
    for (let j = 0; j <= b.length; j++) d[0][j] = j;

    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const custo = a[i - 1] === b[j - 1] ? 0 : 1;
            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + custo);

            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
            }
        }
    }

    return d[a.length][b.length];
}

// Junta trechos que se sobrepõem ou encostam, em ordem, para o destaque não abrir
// uma marca dentro da outra.
export function mesclarTrechos(trechos) {
    const saida = [];

    [...trechos]
        .sort((a, b) => a[0] - b[0])
        .forEach(([inicio, fim]) => {
            const ultimo = saida[saida.length - 1];
            if (ultimo && inicio <= ultimo[1]) {
                ultimo[1] = Math.max(ultimo[1], fim);
            } else {
                saida.push([inicio, fim]);
            }
        });

    return saida;
}

function palavras(campo) {
    const lista = [];
    const re = /[a-z0-9]+/g;
    let m;

    while ((m = re.exec(campo)) !== null) {
        lista.push({ texto: m[0], inicio: m.index });
    }

    return lista;
}

// Letras do termo aparecendo na ordem dentro da palavra, casadas da esquerda para
// a direita. Devolve os índices das letras na palavra, ou null.
function letrasNaOrdem(termo, palavra) {
    const indices = [];
    let pos = 0;

    for (const letra of termo) {
        const i = palavra.indexOf(letra, pos);
        if (i === -1) return null;
        indices.push(i);
        pos = i + 1;
    }

    return indices;
}

// Melhor jeito de um termo bater num campo já normalizado, ou null. Os trechos
// são índices [início, fim) no campo.
function casarTermo(termo, campo) {
    const lista = palavras(campo);

    const comeco = lista.find(p => p.texto.startsWith(termo));
    if (comeco) {
        return { pontos: PONTOS.prefixo, forte: true, trechos: [[comeco.inicio, comeco.inicio + termo.length]] };
    }

    const i = campo.indexOf(termo);
    if (i !== -1) {
        return { pontos: PONTOS.trecho, forte: true, trechos: [[i, i + termo.length]] };
    }

    if (termo.length >= MIN_DIGITACAO) {
        const limite = termo.length >= 7 ? 2 : 1;

        for (const p of lista) {
            // Compara com a palavra inteira e com o começo dela do tamanho do termo,
            // para o erro de digitação valer também com a palavra pela metade.
            const inteira = distancia(termo, p.texto, limite);
            const parcial = distancia(termo, p.texto.slice(0, termo.length), limite);
            const menor = Math.min(inteira, parcial);

            if (menor <= limite) {
                const fim = inteira <= parcial ? p.texto.length : termo.length;
                return { pontos: PONTOS.digitacao - (menor - 1) * 10, forte: false, trechos: [[p.inicio, p.inicio + fim]] };
            }
        }
    }

    if (termo.length >= MIN_ORDEM) {
        let melhor = null;

        for (const p of lista) {
            const indices = letrasNaOrdem(termo, p.texto);
            if (!indices) continue;

            const buracos = indices[indices.length - 1] - indices[0] + 1 - termo.length;
            if (buracos > MAX_BURACOS) continue;

            const pontos = Math.max(PONTOS.ordem - buracos * 2, 5);
            if (!melhor || pontos > melhor.pontos) {
                melhor = { pontos, forte: false, trechos: indices.map(i => [p.inicio + i, p.inicio + i + 1]) };
            }
        }

        return melhor;
    }

    return null;
}

function camposDaRota(rota) {
    const campos = [{ nome: 'nome', texto: normalizar(rota.nome), peso: PESOS.nome }];

    (rota.busca || []).forEach(palavra => {
        campos.push({ nome: 'busca', texto: normalizar(palavra), peso: PESOS.busca });
    });

    if (rota.group) {
        campos.push({ nome: 'group', texto: normalizar(rota.group), peso: PESOS.group });
    }

    return campos;
}

// Pontos de uma rota para os termos digitados, ou null se algum termo não bate em
// campo nenhum. A rota é "forte" quando cada termo teve pelo menos um acerto forte.
function pontuarRota(rota, termos) {
    const campos = camposDaRota(rota);
    let total = 0;
    let forte = true;
    const trechos = [];

    for (const termo of termos) {
        let melhor = 0;
        let forteTermo = false;

        campos.forEach(campo => {
            const acerto = casarTermo(termo, campo.texto);
            if (!acerto) return;

            melhor = Math.max(melhor, acerto.pontos * campo.peso);
            forteTermo = forteTermo || acerto.forte;
            if (campo.nome === 'nome') trechos.push(...acerto.trechos);
        });

        if (melhor === 0) return null;

        total += melhor;
        forte = forte && forteTermo;
    }

    return { pontos: total, forte, trechos };
}

// Devolve `[{ rota, trechos, pontos }]` na ordem de entrada. `trechos` são os
// índices [início, fim) do que bateu em `rota.nome`, já mesclados, para destacar.
export function filtrarMenus(rotas, consulta) {
    const termos = normalizar(consulta).split(' ').filter(Boolean);

    if (termos.length === 0) {
        return rotas.map(rota => ({ rota, trechos: [], pontos: 0 }));
    }

    const acertos = [];
    rotas.forEach(rota => {
        const acerto = pontuarRota(rota, termos);
        if (acerto) acertos.push({ rota, ...acerto });
    });

    const temForte = acertos.some(acerto => acerto.forte);

    return acertos
        .filter(acerto => !temForte || acerto.forte)
        .map(({ rota, trechos, pontos }) => ({ rota, trechos: mesclarTrechos(trechos), pontos }));
}
